import React, { useState, useEffect, useCallback } from 'react';
import API from '../api/api';
import {
  FmcsaStatusBadge, FmcsaResultPanel, FmcsaCheckButton, useFmcsaCheck, isFmcsaVerified,
} from './FmcsaCheck';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || isNaN(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtApptDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', year: '2-digit' });
};

const fmtApptTime = (t) => (t ? String(t).slice(0, 5) : '');

// A blank carrier row for the "new carrier" path
const EMPTY_CARRIER = {
  name: '', usdot_number: '', mc_number: '', contact_name: '', phone: '', email: '', fax: '',
};

const errorText = (err, fallback) => {
  const data = err.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : errs}`)
    .join('; ') || fallback;
};

// ─── Manual appointment entry ─────────────────────────────────────────────────
// C3 Hive has no API, so delivery appointments are booked in their portal and
// keyed in here; pickups rescheduled by phone or in a portal are keyed in the
// same way. Writes through to the stop, which is what the PDF prints.
function AppointmentForm({ loadId, stop, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    appointment_date: stop.appointment_date || '',
    appointment_time: fmtApptTime(stop.appointment_time) || '',
    platform: stop.is_pickup ? 'manual' : 'c3_hive',
    confirmation_number: '',
    notes: '',
  });

  const change = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async () => {
    if (!form.appointment_date || !form.appointment_time) {
      setError('Enter both the appointment date and time.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await API.post(`loads/${loadId}/delivery-appointment/`, { ...form, stop: stop.id });
      setOpen(false);
      onRecorded();
    } catch (err) {
      setError(errorText(err, 'Failed to record the appointment.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 text-xs font-medium text-primary-700 hover:text-primary-900 underline"
      >
        {stop.appointment_date ? 'Change appointment' : 'Add appointment manually'}
      </button>
    );
  }

  return (
    <div className="mt-2 rounded border border-primary-200 bg-primary-50 p-3">
      <p className="text-xs text-gray-600 mb-2">
        {stop.is_pickup
          ? 'Confirm the new time with the shipper, then key it in here.'
          : 'Book the appointment in C3 Hive, then key the confirmed time in here.'}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-medium text-gray-600">Date</label>
          <input type="date" name="appointment_date" value={form.appointment_date} onChange={change}
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600">Time</label>
          <input type="time" name="appointment_time" value={form.appointment_time} onChange={change}
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600">Booked in</label>
          <select name="platform" value={form.platform} onChange={change}
                  className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white">
            <option value="c3_hive">C3 Hive</option>
            <option value="manual">Other / phone or email</option>
            <option value="opendock">OpenDock</option>
            <option value="aldi_hub">Aldi Hub</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600">Confirmation #</label>
          <input type="text" name="confirmation_number" value={form.confirmation_number} onChange={change}
                 placeholder="optional"
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600">Notes for the carrier</label>
          <input type="text" name="notes" value={form.notes} onChange={change}
                 placeholder="e.g. check in at guard shack"
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
      </div>
      {error && <p className="text-[11px] text-red-600 mt-2">{error}</p>}
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={submit} disabled={saving}
                className="px-2.5 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save appointment'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setError(null); }}
                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Stop lines on the PDF ────────────────────────────────────────────────────
// The Pick Up / Delivery Numbers, Confirm Numbers, Driver Work and SLIC lines
// each stop block prints. Blank fields fall back to the defaults: every PO on a
// pickup, the customer's load number on a delivery.
function StopDetailsForm({ shipmentId, stop, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);

  const kind = stop.is_pickup ? 'Pick Up' : 'Delivery';

  const start = () => {
    setForm({
      reference_number: stop.reference_number || stop.printed_numbers || '',
      confirm_numbers: stop.confirm_numbers || stop.printed_confirm_numbers || '',
      driver_work: stop.driver_work || '',
      slic: stop.slic || '',
    });
    setError(null);
    setOpen(true);
  };

  const change = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Saving a default unchanged leaves the stop on the default, so it keeps
      // following the shipment if a re-tender changes the POs later.
      const keep = (value, own, printed) =>
        (!own && value.trim() === (printed || '') ? '' : value.trim());
      const payload = {
        reference_number: keep(form.reference_number, stop.reference_number, stop.printed_numbers),
        confirm_numbers: keep(form.confirm_numbers, stop.confirm_numbers, stop.printed_confirm_numbers),
        driver_work: form.driver_work.trim(),
        slic: form.slic.trim(),
      };
      const res = await API.patch(
        `shipments/${shipmentId}/stops/${stop.id}/rate-confirmation-fields/`, payload,
      );
      setOpen(false);
      onSaved(res.data);
    } catch (err) {
      setError(errorText(err, 'Failed to save the stop details.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={start}
        className="mt-1 text-xs font-medium text-primary-700 hover:text-primary-900 underline"
      >
        Edit {kind.toLowerCase()} numbers
      </button>
    );
  }

  return (
    <div className="mt-2 rounded border border-primary-200 bg-primary-50 p-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600">{kind} Numbers</label>
          <textarea name="reference_number" rows="2" value={form.reference_number} onChange={change}
                    className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
          <p className="text-[11px] text-gray-500 mt-0.5">
            {stop.is_pickup
              ? 'Remove any POs the carrier doesn’t need. Clear it to go back to every PO on the stop.'
              : 'Clear it to go back to the customer’s load number.'}
          </p>
        </div>
        <div className="col-span-2">
          <label className="block text-[11px] font-medium text-gray-600">Confirm Numbers</label>
          <input type="text" name="confirm_numbers" value={form.confirm_numbers} onChange={change}
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600">Driver Work</label>
          <input type="text" name="driver_work" value={form.driver_work} onChange={change}
                 placeholder="e.g. No Touch"
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600">SLIC</label>
          <input type="text" name="slic" value={form.slic} onChange={change}
                 placeholder="N/A"
                 className="mt-0.5 block w-full border border-gray-300 rounded px-2 py-1 text-xs" />
        </div>
      </div>
      {error && <p className="text-[11px] text-red-600 mt-2">{error}</p>}
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={submit} disabled={saving}
                className="px-2.5 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setError(null); }}
                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
function RateConfirmationModal({ isOpen, onClose, shipment, onSent }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);

  const [carriers, setCarriers] = useState([]);
  const [carrierId, setCarrierId] = useState('');
  const [newCarrier, setNewCarrier] = useState(EMPTY_CARRIER);
  const [saveToDirectory, setSaveToDirectory] = useState(true);

  // FMCSA check for a carrier typed in here (directory carriers carry their own).
  const fmcsa = useFmcsaCheck();
  const { reset: resetFmcsa } = fmcsa;
  const [fmcsaCheckedFor, setFmcsaCheckedFor] = useState(null);
  const [recheckingCarrier, setRecheckingCarrier] = useState(false);

  const [form, setForm] = useState(null);          // the editable rate-con payload
  const [stops, setStops] = useState([]);
  const [history, setHistory] = useState([]);      // confirmations already on this shipment
  const [draftId, setDraftId] = useState(null);    // persisted draft, once created

  const [recipients, setRecipients] = useState('');
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);

  const loadId = shipment?.load;

  // ── Load prefill + directory + history ─────────────────────────────────────
  const loadEverything = useCallback(async () => {
    if (!shipment) return;
    setLoading(true);
    setError(null);
    try {
      const [defaultsRes, carriersRes, historyRes] = await Promise.all([
        API.get(`shipments/${shipment.id}/rate-confirmation-defaults/`),
        API.get('carriers/', { params: { is_active: true, page_size: 200 } }),
        API.get('rate-confirmations/', { params: { shipment: shipment.id } }),
      ]);

      const { defaults, stops: apiStops, existing_draft } = defaultsRes.data;
      setStops(apiStops || []);

      const carrierList = carriersRes.data?.results ?? carriersRes.data ?? [];
      setCarriers(carrierList);

      const historyList = historyRes.data?.results ?? historyRes.data ?? [];
      setHistory(historyList);

      // Resume an unsent draft rather than starting a second one.
      const seed = existing_draft || defaults;
      setDraftId(existing_draft ? existing_draft.id : null);
      setCarrierId(seed.carrier ? String(seed.carrier) : '');
      setForm({
        carrier_name: seed.carrier_name || '',
        carrier_usdot: seed.carrier_usdot || '',
        carrier_mc: seed.carrier_mc || '',
        carrier_contact_name: seed.carrier_contact_name || '',
        carrier_phone: seed.carrier_phone || '',
        carrier_email: seed.carrier_email || '',
        carrier_fax: seed.carrier_fax || '',
        carrier_rate: seed.carrier_rate ?? '',
        currency: seed.currency || 'USD',
        equipment_type: seed.equipment_type || '',
        equipment_length: seed.equipment_length || '',
        pre_cooled_temp: seed.pre_cooled_temp || '',
        load_temp: seed.load_temp || '',
        tarps: seed.tarps || '',
        declared_value: seed.declared_value ?? '',
        attributes: seed.attributes || '',
        notes: seed.notes || '',
        booked_by_name: seed.booked_by_name || '',
        booked_by_phone: seed.booked_by_phone || '',
        booked_by_email: seed.booked_by_email || '',
      });
      setRecipients(seed.carrier_email || '');
    } catch (err) {
      setError(errorText(err, 'Failed to load rate confirmation details.'));
    } finally {
      setLoading(false);
    }
  }, [shipment]);

  useEffect(() => {
    if (isOpen) loadEverything();
  }, [isOpen, loadEverything]);

  // Re-read just the stops after an appointment is keyed in, so unsaved edits
  // elsewhere in the form survive.
  const refreshStops = useCallback(async () => {
    try {
      const res = await API.get(`shipments/${shipment.id}/rate-confirmation-defaults/`);
      setStops(res.data.stops || []);
    } catch (err) {
      setError(errorText(err, 'Failed to refresh the stops.'));
    }
  }, [shipment]);

  const replaceStop = (updated) =>
    setStops((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));

  // Reset transient state when the modal closes
  useEffect(() => {
    if (!isOpen) {
      setBanner(null);
      setError(null);
      setNewCarrier(EMPTY_CARRIER);
      resetFmcsa();
      setFmcsaCheckedFor(null);
    }
  }, [isOpen, resetFmcsa]);

  const change = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // Picking a directory carrier fills the snapshot fields it owns.
  const pickCarrier = (id) => {
    setCarrierId(id);
    const chosen = carriers.find((c) => String(c.id) === String(id));
    if (!chosen) return;
    setForm((p) => ({
      ...p,
      carrier_name: chosen.name || '',
      carrier_usdot: chosen.usdot_number || '',
      carrier_mc: chosen.mc_number || '',
      carrier_contact_name: chosen.contact_name || '',
      carrier_phone: chosen.phone || '',
      carrier_email: chosen.email || '',
      carrier_fax: chosen.fax || '',
    }));
    setRecipients(chosen.email || '');
  };

  const buildPayload = () => {
    const payload = { shipment: shipment.id, ...form };
    if (carrierId) payload.carrier = Number(carrierId);
    // Blank decimals must go as null, not '' — DRF rejects the empty string.
    ['carrier_rate', 'declared_value'].forEach((f) => {
      if (payload[f] === '' || payload[f] === undefined) payload[f] = null;
    });
    return payload;
  };

  // Create the directory entry first when the operator typed a new carrier.
  const ensureCarrier = async () => {
    if (carrierId || !saveToDirectory) return null;
    const name = (newCarrier.name || form.carrier_name || '').trim();
    if (!name) return null;
    const res = await API.post('carriers/', { ...newCarrier, name });
    setCarriers((prev) => [...prev, res.data]);
    setCarrierId(String(res.data.id));
    return res.data.id;
  };

  // Persist the draft (create or update) and hand back the saved record.
  const persistDraft = async () => {
    const createdCarrierId = await ensureCarrier();
    const payload = buildPayload();
    if (createdCarrierId) payload.carrier = createdCarrierId;

    if (draftId) {
      const res = await API.patch(`rate-confirmations/${draftId}/`, payload);
      return res.data;
    }
    const res = await API.post('rate-confirmations/', payload);
    setDraftId(res.data.id);
    return res.data;
  };

  const handleSaveDraft = async () => {
    setSending(true);
    setError(null);
    setBanner(null);
    try {
      const saved = await persistDraft();
      setBanner(`Draft ${saved.confirmation_number} saved.`);
      setHistory((prev) => [saved, ...prev.filter((h) => h.id !== saved.id)]);
    } catch (err) {
      setError(errorText(err, 'Failed to save the rate confirmation.'));
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    setSending(true);
    setError(null);
    try {
      const saved = await persistDraft();
      setHistory((prev) => [saved, ...prev.filter((h) => h.id !== saved.id)]);
      const res = await API.get(`rate-confirmations/${saved.id}/pdf/`, {
        params: { inline: 1 },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to claim the blob before revoking it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(errorText(err, 'Failed to generate the PDF preview.'));
    } finally {
      setSending(false);
    }
  };

  // ── FMCSA ────────────────────────────────────────────────────────────────
  const carrierIdentityKey = () =>
    [form?.carrier_usdot, form?.carrier_mc, form?.carrier_phone].map((v) => (v || '').trim()).join('|');

  const runFmcsaCheck = async () => {
    const key = carrierIdentityKey();
    const result = await fmcsa.run({
      usdot_number: form.carrier_usdot, mc_number: form.carrier_mc, phone: form.carrier_phone,
    });
    setFmcsaCheckedFor(key);
    if (result?.legal_name && !form.carrier_name.trim()) applyFmcsaName(result.legal_name);
  };

  const applyFmcsaName = (name) => {
    setForm((p) => ({ ...p, carrier_name: name }));
    setNewCarrier((p) => ({ ...p, name }));
  };

  const recheckDirectoryCarrier = async () => {
    setRecheckingCarrier(true);
    try {
      const res = await API.post(`carriers/${carrierId}/fmcsa-check/`);
      setCarriers((prev) => prev.map((c) => (c.id === res.data.id ? res.data : c)));
    } catch {
      setError("Couldn't re-check this carrier against FMCSA.");
    } finally {
      setRecheckingCarrier(false);
    }
  };

  // Why this carrier isn't FMCSA-verified, or null when it is.
  const fmcsaWarning = () => {
    if (carrierId) {
      const chosen = carriers.find((c) => String(c.id) === String(carrierId));
      if (!chosen || isFmcsaVerified(chosen.fmcsa_status)) return null;
      return `FMCSA check: ${chosen.fmcsa_status_display || 'not checked'}.`;
    }
    const current = fmcsa.result && fmcsaCheckedFor === carrierIdentityKey();
    if (current && isFmcsaVerified(fmcsa.result.status)) return null;
    return current ? `FMCSA check: ${fmcsa.result.message}` : "This carrier hasn't been verified with FMCSA.";
  };

  const handleSend = async () => {
    const to = recipients.split(',').map((r) => r.trim()).filter(Boolean);
    if (to.length === 0) {
      setError('Enter at least one recipient email address.');
      return;
    }
    const warning = fmcsaWarning();
    const prompt = `Email this rate confirmation to ${to.join(', ')}?`;
    if (!window.confirm(warning ? `⚠ ${warning}\n\n${prompt}` : prompt)) return;

    setSending(true);
    setError(null);
    setBanner(null);
    try {
      const saved = await persistDraft();
      const res = await API.post(`rate-confirmations/${saved.id}/send/`, {
        recipients: to,
        cc: cc.split(',').map((c) => c.trim()).filter(Boolean),
      });
      setBanner(`Rate confirmation ${res.data.confirmation_number} sent to ${to.join(', ')}.`);
      onSent?.(res.data);
      // The sent confirmation is frozen — reload so the form reflects that rather
      // than holding a draft id that can no longer be edited.
      await loadEverything();
    } catch (err) {
      setError(errorText(err, 'Failed to send the rate confirmation.'));
    } finally {
      setSending(false);
    }
  };

  const handleRevise = async (id) => {
    setSending(true);
    setError(null);
    try {
      const res = await API.post(`rate-confirmations/${id}/revise/`);
      setBanner(`Opened revision ${res.data.confirmation_number}. Edit and send it below.`);
      await loadEverything();
    } catch (err) {
      setError(errorText(err, 'Failed to open a revision.'));
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = async (rc) => {
    try {
      const res = await API.get(`rate-confirmations/${rc.id}/pdf/`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Load Conf# ${rc.confirmation_number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errorText(err, 'Failed to download the PDF.'));
    }
  };

  if (!isOpen || !shipment) return null;

  const usingNewCarrier = !carrierId;
  const chosenCarrier = carriers.find((c) => String(c.id) === String(carrierId));
  const fmcsaResultIsCurrent = fmcsa.result && fmcsaCheckedFor === carrierIdentityKey();
  const hasCarrierIdentity = Boolean((form?.carrier_usdot || '').trim() || (form?.carrier_mc || '').trim());
  const unscheduledDeliveries = stops.filter((s) => !s.is_pickup && s.needs_appointment);
  const total = fmtMoney(form?.carrier_rate);

  const field = (label, name, props = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input
        name={name}
        value={form[name] ?? ''}
        onChange={change}
        {...props}
        className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-8 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white mb-16">

        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Rate Confirmation — Load {shipment.pro_number}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {shipment.shipper_name || 'Customer'}
              {shipment.cust_po_number ? ` · PO ${shipment.cust_po_number}` : ''}
              {' · '}Generate the PDF and email it to the carrier for signature.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
        )}
        {banner && (
          <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">{banner}</div>
        )}

        {loading || !form ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-5">

            {/* ── Carrier ────────────────────────────────────────────────── */}
            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Carrier</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">From your carrier directory</label>
                  <select
                    value={carrierId}
                    onChange={(e) => pickCarrier(e.target.value)}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">— New carrier (enter below) —</option>
                    {carriers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name || c.name}{isFmcsaVerified(c.fmcsa_status) ? '' : ' — not FMCSA verified'}
                      </option>
                    ))}
                  </select>
                </div>
                {usingNewCarrier && (
                  <div className="flex items-end">
                    <label className="inline-flex items-center text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={saveToDirectory}
                        onChange={(e) => setSaveToDirectory(e.target.checked)}
                        className="mr-2 h-3.5 w-3.5 rounded border-gray-300 text-primary-600"
                      />
                      Save this carrier to the directory for next time
                    </label>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {field('Carrier name *', 'carrier_name', {
                  onChange: (e) => {
                    change(e);
                    if (usingNewCarrier) setNewCarrier((p) => ({ ...p, name: e.target.value }));
                  },
                  placeholder: 'Vynera Transportation Inc',
                })}
                <div>
                  <label className="block text-xs font-medium text-gray-600">USDOT</label>
                  <input
                    name="carrier_usdot"
                    value={form.carrier_usdot}
                    onChange={(e) => { change(e); if (usingNewCarrier) setNewCarrier((p) => ({ ...p, usdot_number: e.target.value })); }}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">MC / docket</label>
                  <input
                    name="carrier_mc"
                    value={form.carrier_mc}
                    onChange={(e) => { change(e); if (usingNewCarrier) setNewCarrier((p) => ({ ...p, mc_number: e.target.value })); }}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Contact</label>
                  <input
                    name="carrier_contact_name"
                    value={form.carrier_contact_name}
                    onChange={(e) => { change(e); if (usingNewCarrier) setNewCarrier((p) => ({ ...p, contact_name: e.target.value })); }}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Phone</label>
                  <input
                    name="carrier_phone"
                    value={form.carrier_phone}
                    onChange={(e) => { change(e); if (usingNewCarrier) setNewCarrier((p) => ({ ...p, phone: e.target.value })); }}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Dispatch email</label>
                  <input
                    name="carrier_email"
                    type="email"
                    value={form.carrier_email}
                    onChange={(e) => {
                      change(e);
                      if (usingNewCarrier) setNewCarrier((p) => ({ ...p, email: e.target.value }));
                      setRecipients(e.target.value);
                    }}
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* FMCSA: verify a typed-in carrier; show the stored result for a directory one. */}
              {usingNewCarrier ? (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <FmcsaCheckButton onClick={runFmcsaCheck} checking={fmcsa.checking} disabled={!hasCarrierIdentity} />
                    <span className="text-xs text-gray-500">
                      {fmcsa.result && !fmcsaResultIsCurrent
                        ? 'Details changed — verify again.'
                        : 'Checks the phone against what FMCSA has on file for this USDOT / MC.'}
                    </span>
                  </div>
                  {fmcsaResultIsCurrent && <FmcsaResultPanel result={fmcsa.result} onUseName={applyFmcsaName} />}
                </div>
              ) : chosenCarrier && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <FmcsaStatusBadge status={chosenCarrier.fmcsa_status} checkedAt={chosenCarrier.fmcsa_checked_at} />
                  {!isFmcsaVerified(chosenCarrier.fmcsa_status) && chosenCarrier.fmcsa_result?.message && (
                    <span>{chosenCarrier.fmcsa_result.message}</span>
                  )}
                  <button type="button" onClick={recheckDirectoryCarrier} disabled={recheckingCarrier}
                          className="font-medium text-primary-700 hover:text-primary-900 underline disabled:opacity-50">
                    {recheckingCarrier ? 'Checking…' : chosenCarrier.fmcsa_status ? 'Re-check' : 'Check FMCSA'}
                  </button>
                </div>
              )}
            </div>

            {/* ── Rate & equipment ───────────────────────────────────────── */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="text-md font-semibold text-gray-900">Rate &amp; Equipment</h4>
                <span className="text-sm text-gray-500">
                  Carrier pay <span className="font-semibold text-gray-900">{total}</span>
                  {shipment.rate ? <span className="text-xs text-gray-400"> · customer rate {fmtMoney(shipment.rate)}</span> : null}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {field('Carrier rate *', 'carrier_rate', { type: 'number', step: '0.01', min: '0', placeholder: '2700.00' })}
                {field('Equipment', 'equipment_type', { placeholder: 'Dry Van' })}
                {field('Length', 'equipment_length', { placeholder: "53'" })}
                {field('Pre-cooled temp', 'pre_cooled_temp', { placeholder: 'ambient' })}
                {field('Load temp', 'load_temp', { placeholder: 'Dry' })}
                {field('Tarps', 'tarps', { placeholder: 'Undefined' })}
                {field('Declared value', 'declared_value', { type: 'number', step: '0.01', min: '0', placeholder: '150000' })}
                {field('Attributes', 'attributes', { placeholder: 'Seal' })}
              </div>
            </div>

            {/* ── Stops & appointments ───────────────────────────────────── */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-1">Stops &amp; Appointments</h4>
              <p className="text-xs text-gray-500 mb-3">
                These print on the confirmation exactly as shown. Delivery appointments booked
                in C3 Hive have to be keyed in here — there is no API to pull them from yet.
                Pickups list every PO on the stop; edit the numbers to trim them.
              </p>

              {unscheduledDeliveries.length > 0 && (
                <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  <strong>
                    {unscheduledDeliveries.length} delivery stop{unscheduledDeliveries.length === 1 ? '' : 's'} ha
                    {unscheduledDeliveries.length === 1 ? 's' : 've'} no appointment.
                  </strong>{' '}
                  The PDF will print “TBD — carrier to schedule” unless you add the time below.
                </div>
              )}

              <div className="space-y-2">
                {stops.map((stop) => (
                  <div key={stop.id} className="bg-white border rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            stop.is_pickup ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {stop.is_pickup ? 'Pick Up' : 'Delivery'}
                          </span>
                          <span className="text-sm font-medium text-gray-800 truncate">
                            Stop {stop.sequence}{stop.name ? ` — ${stop.name}` : ''}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{stop.full_address || '—'}</p>
                        {(stop.contact_name || stop.contact_phone) && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {[stop.contact_name, stop.contact_phone].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <p className="text-xs text-gray-600 mt-1 break-words">
                          <span className="text-gray-400">{stop.is_pickup ? 'Pick Up' : 'Delivery'} #:</span>{' '}
                          {stop.printed_numbers || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-600 break-words">
                          <span className="text-gray-400">Confirm:</span> {stop.printed_confirm_numbers || 'N/A'}
                          {stop.driver_work && <> · <span className="text-gray-400">Driver Work:</span> {stop.driver_work}</>}
                          {stop.slic && <> · <span className="text-gray-400">SLIC:</span> {stop.slic}</>}
                        </p>
                        <StopDetailsForm shipmentId={shipment.id} stop={stop} onSaved={replaceStop} />
                      </div>
                      <div className="text-right shrink-0">
                        {stop.appointment_date ? (
                          <>
                            <div className="text-sm font-semibold text-gray-900 uppercase">
                              {fmtApptDate(stop.appointment_date)}
                            </div>
                            {stop.appointment_time && (
                              <div className="text-sm font-semibold text-gray-900">
                                at {fmtApptTime(stop.appointment_time)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-xs font-semibold text-amber-700">TBD</span>
                        )}
                        {loadId && (
                          <AppointmentForm loadId={loadId} stop={stop} onRecorded={refreshStops} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {stops.length === 0 && (
                  <p className="text-xs text-gray-500">This shipment has no stops recorded.</p>
                )}
              </div>
            </div>

            {/* ── Notes & booked by ──────────────────────────────────────── */}
            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Notes &amp; Contact</h4>
              <div>
                <label className="block text-xs font-medium text-gray-600">Load notes for the carrier</label>
                <textarea
                  name="notes"
                  rows="2"
                  value={form.notes}
                  onChange={change}
                  placeholder="Anything the driver needs to know beyond the standard terms…"
                  className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Your standard terms, route directions and facility notes are added automatically.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                {field('Booked by', 'booked_by_name')}
                {field('Phone', 'booked_by_phone')}
                {field('Email', 'booked_by_email', { type: 'email' })}
              </div>
            </div>

            {/* ── Send ───────────────────────────────────────────────────── */}
            <div className="bg-white border-2 border-primary-200 rounded-lg p-4">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Send to Carrier</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">To (comma-separated)</label>
                  <input
                    type="text"
                    value={recipients}
                    onChange={(e) => setRecipients(e.target.value)}
                    placeholder="dispatch@carrier.com"
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">CC (optional)</label>
                  <input
                    type="text"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="ap@yourcompany.com"
                    className="mt-0.5 block w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* ── Already sent ───────────────────────────────────────────── */}
            {history.filter((h) => h.status !== 'draft').length > 0 && (
              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Confirmation History</h4>
                <div className="space-y-2">
                  {history.filter((h) => h.status !== 'draft').map((rc) => (
                    <div key={rc.id} className="flex items-center justify-between bg-white border rounded p-2.5 text-sm">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900">{rc.confirmation_number}</span>
                        <span className="text-gray-500"> · {rc.carrier_name} · {fmtMoney(rc.total_amount)}</span>
                        {rc.sent_at && (
                          <div className="text-xs text-gray-400">
                            Sent {new Date(rc.sent_at).toLocaleString()} to {(rc.sent_to || []).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          rc.status === 'signed' ? 'bg-green-100 text-green-800'
                            : rc.status === 'void' ? 'bg-gray-200 text-gray-600'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {rc.status_display || rc.status}
                        </span>
                        <button type="button" onClick={() => downloadPdf(rc)}
                                className="text-xs text-primary-700 hover:text-primary-900 underline">
                          PDF
                        </button>
                        {rc.status !== 'void' && (
                          <button type="button" onClick={() => handleRevise(rc.id)} disabled={sending}
                                  className="text-xs text-primary-700 hover:text-primary-900 underline disabled:opacity-50">
                            Revise
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Actions ────────────────────────────────────────────────── */}
            <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                Close
              </button>
              <button type="button" onClick={handleSaveDraft} disabled={sending}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                Save Draft
              </button>
              <button type="button" onClick={handlePreview} disabled={sending}
                      className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 disabled:opacity-50">
                Preview PDF
              </button>
              <button type="button" onClick={handleSend} disabled={sending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                {sending ? 'Working…' : 'Send to Carrier'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RateConfirmationModal;
